import { useState, useEffect } from "react";
import { getGcRequirements } from "../services/projectService";

interface ApiError {
  message?: string;
}

type ChipVariant = "blue" | "amber" | "green" | "purple";

interface RequirementRow {
  id: string;
  label: string;
  detail: string;
  chip: { label: string; variant: ChipVariant } | null;
  required: string;
  isOptional: boolean;
  isAccepted: boolean;
}

interface RequirementSection {
  id: number;
  title: string;
  icon: string;
  rows: RequirementRow[];
}

interface AckData {
  fullName?: string;
  designation?: string;
  companyName?: string;
  gcSignatureUrl?: string;
  signedAt?: string | null;
}

interface RawRequirement {
  id: string;
  category: string;
  categoryOrder: number;
  title: string;
  description?: string;
  value?: string;
  requiredBy?: string | string[];
  isAccepted?: boolean | "yes" | "no";
  itemOrder: number;
}

interface RawCategoryGroup {
  id: number;
  title: string;
  icon: string;
  categoryOrder: number;
  rows: (RequirementRow & { _order: number })[];
}

const CATEGORY_ICONS: Record<string, string> = {
  "Draw & Disbursement Requirements": "💰",
  "Draw Requirements": "💰",
  "Lien Waiver Requirements": "📝",
  "Lien Waivers": "📝",
  "Insurance & Bond Requirements": "🛡",
  "Insurance Requirements": "🛡",
  "Inspections & CSC Reporting": "🔍",
  Inspections: "🔍",
  "Budget, Soft Costs & Contingency": "📊",
  "Budget Requirements": "📊",
};

const CHIP_COLOR_MAP: Record<string, ChipVariant> = {
  "Not Required — This Project": "green",
  "Monthly · First Wednesday": "blue",
  "$5,000 Bank Approval Threshold": "amber",
  "10% Retainage": "purple",
};

const groupRequirements = (requirements: RawRequirement[]): RequirementSection[] => {
  const map: Record<string, RawCategoryGroup> = {};

  requirements.forEach((item) => {
    if (!map[item.category]) {
      map[item.category] = {
        id: item.categoryOrder,
        title: item.category,
        icon: CATEGORY_ICONS[item.category] ?? "📋",
        categoryOrder: item.categoryOrder,
        rows: [],
      };
    }
    map[item.category].rows.push({
      id: item.id,
      label: item.title,
      detail: item.description ?? "",
      chip: item.value
        ? { label: item.value, variant: CHIP_COLOR_MAP[item.value] ?? "blue" }
        : null,
      required:
        Array.isArray(item.requiredBy) && item.requiredBy.length > 0
          ? `★ Required by ${item.requiredBy.join(" & ")}`
          : "○ Optional for this project",
      isOptional: !Array.isArray(item.requiredBy) || item.requiredBy.length === 0,
      isAccepted: item.isAccepted === true || item.isAccepted === "yes",
      _order: item.itemOrder,
    });
  });

  return Object.values(map)
    .sort((a, b) => a.categoryOrder - b.categoryOrder)
    .map(({ categoryOrder: _, ...section }) => ({
      ...section,
      rows: section.rows
        .sort((a, b) => a._order - b._order)
        .map(({ _order: __, ...row }) => row),
    }));
};

const useGetGcRequirements = (projectId: string | undefined) => {
  const [sections, setSections] = useState<RequirementSection[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [initialAcked, setInitialAcked] = useState<Set<string> | null>(null);
  const [ack, setAck] = useState<AckData | null>(null);
  const [isEditable, setIsEditable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      const { data, error: apiError } = await getGcRequirements(projectId) as {
        data: {
          data?: {
            requirements?: RawRequirement[];
            ack?: AckData;
            isEditable?: boolean;
          };
        } | null;
        error: ApiError | null;
      };

      if (cancelled) return;
      setLoading(false);

      if (apiError) {
        setError(apiError.message || "Failed to load requirements.");
        return;
      }

      const payload = data?.data ?? {};
      const reqs: RawRequirement[] = payload.requirements ?? [];
      setSections(groupRequirements(reqs));
      setTotalCount(reqs.length);
      setInitialAcked(
        new Set(
          reqs
            .filter((r) => r.isAccepted === "yes" || r.isAccepted === true)
            .map((r) => r.id),
        ),
      );
      setAck(payload.ack ?? null);
      setIsEditable(payload.isEditable ?? true);
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return { sections, totalCount, initialAcked, ack, isEditable, loading, error };
};

export default useGetGcRequirements;
